import type { InspectionEvent, Observer } from 'xstate';
import { createInspectorEntryBuilder } from './entryBuilder';
import {
  getVoiceLiveInspectorChannelName,
  getVoiceLiveInspectorWindowName,
  VOICE_LIVE_INSPECTOR_MAX_ENTRIES,
  VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION,
  VOICE_LIVE_INSPECTOR_ROUTE,
  type InspectorEntry,
  type InspectorReceiverMessage,
} from './protocol';

const HEARTBEAT_INTERVAL_MS = 5_000;

interface InspectorChannel {
  close: () => void;
  onmessage: ((event: MessageEvent<unknown>) => void) | null;
  postMessage: (message: unknown) => void;
}

type InspectorChannelConstructor = new (name: string) => InspectorChannel;

export interface VoiceLiveInspectorSender {
  dispose: () => void;
  inspect: Observer<InspectionEvent>;
  sessionId: string;
}

interface CreateVoiceLiveInspectorSenderOptions {
  channelConstructor?: InspectorChannelConstructor;
  createSessionId?: () => string;
  heartbeatIntervalMs?: number;
  maxEntries?: number;
  sourceWindow?: Window;
}

const isReceiverMessage = (
  value: unknown,
  sessionId: string,
): value is InspectorReceiverMessage => {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<InspectorReceiverMessage>;

  return (
    message.protocolVersion === VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION &&
    message.sessionId === sessionId &&
    (message.kind === 'receiver-ready' || message.kind === 'clear')
  );
};

const defaultSessionId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const bytes = new Uint32Array(4);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(8, '0')).join('');
};

export const createVoiceLiveInspectorSender = (
  options: CreateVoiceLiveInspectorSenderOptions = {},
): VoiceLiveInspectorSender | undefined => {
  const sourceWindow = options.sourceWindow ?? (typeof window === 'undefined' ? undefined : window);
  const Channel =
    options.channelConstructor ??
    (typeof BroadcastChannel === 'undefined' ? undefined : BroadcastChannel);

  if (!sourceWindow || !Channel) return undefined;

  const sessionId = (options.createSessionId ?? defaultSessionId)();
  const channel = new Channel(getVoiceLiveInspectorChannelName(sessionId));
  const buildEntry = createInspectorEntryBuilder();
  const entries: InspectorEntry[] = [];
  const maxEntries = options.maxEntries ?? VOICE_LIVE_INSPECTOR_MAX_ENTRIES;
  let disposed = false;

  const inspectorUrl = new URL(VOICE_LIVE_INSPECTOR_ROUTE, sourceWindow.location.origin);
  const inspectorWindow = sourceWindow.open(
    inspectorUrl.toString(),
    getVoiceLiveInspectorWindowName(sessionId),
  );
  if (!inspectorWindow) {
    console.warn('Voice Live inspector popup was blocked; allow popups and reload the session.');
  }

  channel.onmessage = (messageEvent) => {
    if (!isReceiverMessage(messageEvent.data, sessionId)) return;

    if (messageEvent.data.kind === 'clear') {
      entries.length = 0;
      buildEntry.reset();
      return;
    }

    channel.postMessage({
      kind: 'replay',
      protocolVersion: VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION,
      sessionId,
      entries: [...entries],
    });
  };

  const inspect: Observer<InspectionEvent> = {
    next: (inspectionEvent) => {
      if (disposed) return;

      const entry = buildEntry.build(inspectionEvent);
      if (!entry) return;

      entries.push(entry);
      if (entries.length > maxEntries) entries.shift();

      channel.postMessage({
        kind: 'entry',
        protocolVersion: VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION,
        sessionId,
        entry,
      });
    },
  };

  const heartbeatIntervalMs = options.heartbeatIntervalMs ?? HEARTBEAT_INTERVAL_MS;
  const heartbeatId = heartbeatIntervalMs
    ? sourceWindow.setInterval(() => {
        channel.postMessage({
          kind: 'heartbeat',
          protocolVersion: VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION,
          sessionId,
          timestamp: Date.now(),
        });
      }, heartbeatIntervalMs)
    : undefined;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    channel.postMessage({
      kind: 'source-closed',
      protocolVersion: VOICE_LIVE_INSPECTOR_PROTOCOL_VERSION,
      sessionId,
    });
    if (heartbeatId !== undefined) sourceWindow.clearInterval(heartbeatId);
    sourceWindow.removeEventListener('pagehide', dispose);
    channel.close();
  };

  sourceWindow.addEventListener('pagehide', dispose);

  return { dispose, inspect, sessionId };
};
